import { useMemo } from 'react';
import useSWR from 'swr';
import { Sparkles, ShoppingCart, Check } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/lib/api';
import { useCart, formatAUD } from '@/context/CartContext';
import { resolveImage } from '@/lib/resolveImage';
import { trackEvent } from '@/lib/analytics';

const fetcher = (url) => fetch(`${API}${url}`).then((r) => r.ok ? r.json() : []);

/**
 * "Cook this recipe" pairings block for /recipes/:slug.
 *
 * Given the recipe's `productSlug` (the exact cut it calls for) plus a
 * `pairingSlugs` array of 2 complementary cuts, render a 3-card row with
 * one-tap "Add to cart" buttons. Falls back gracefully when a slug can't
 * be resolved (product removed from catalog, etc).
 */
export default function RecipePairings({ primarySlug, pairingSlugs = [], recipeTitle }) {
  const { data: allProducts } = useSWR('/products', fetcher);
  const { add, items } = useCart();

  const products = useMemo(() => {
    if (!Array.isArray(allProducts)) return [];
    const byId = new Map(allProducts.map((p) => [p.id, p]));
    const slugs = [primarySlug, ...pairingSlugs].filter(Boolean);
    return slugs.map((s) => byId.get(s)).filter(Boolean);
  }, [allProducts, primarySlug, pairingSlugs]);

  if (!products.length) return null;

  const cartIds = new Set(items.map((i) => i.product_id));

  const onAddOne = (p) => {
    add({
      id: p.id, name: p.name, category: p.category, unit: p.unit,
      price: p.price, min_qty: p.min_qty, step_qty: p.step_qty, image_url: p.image_url,
    });
    toast.success(`Added ${p.name} to cart`);
  };

  const onAddAll = () => {
    products.forEach((p) => {
      if (cartIds.has(p.id)) return; // don't double-add
      add({
        id: p.id, name: p.name, category: p.category, unit: p.unit,
        price: p.price, min_qty: p.min_qty, step_qty: p.step_qty, image_url: p.image_url,
      });
    });
    trackEvent('Cart Add All', {
      source: 'recipe',
      recipe: recipeTitle || 'unknown',
      count: products.length,
    });
    toast.success(`Added ${products.length} cuts for ${recipeTitle} to cart`);
  };

  return (
    <section
      data-testid="recipe-pairings"
      className="mt-20 pt-14 border-t border-black/[0.08] no-print"
    >
      <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-[var(--mw-primary)] mb-3">
            <Sparkles size={12} /> Cook this recipe
          </div>
          <h2 className="font-display font-light text-3xl sm:text-4xl text-black tracking-tight">
            The exact cuts you&apos;ll need
          </h2>
          <p className="mt-3 text-black/60 max-w-xl leading-relaxed">
            Halal-certified, cold-chain delivered across Perth. Tap to add
            each one, or grab the full trio in a single click.
          </p>
        </div>
        <button
          type="button"
          onClick={onAddAll}
          data-testid="recipe-add-all"
          style={{ color: '#fff' }}
          className="inline-flex items-center gap-2 bg-[var(--mw-primary)] hover:bg-[var(--mw-primary-hover)] text-[11px] uppercase tracking-[0.22em] px-6 py-3 transition-colors"
        >
          <ShoppingCart size={14} /> Add all {products.length} to cart
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {products.map((p, i) => {
          const inCart = cartIds.has(p.id);
          const isPrimary = i === 0;
          return (
            <div
              key={p.id}
              data-testid={`recipe-pairing-${p.id}`}
              className={`relative flex flex-col border transition-colors ${
                isPrimary
                  ? 'border-[var(--mw-primary)]/40 bg-white'
                  : 'border-black/10 bg-white hover:border-[var(--mw-primary)]/40'
              }`}
            >
              {isPrimary && (
                <div className="absolute top-3 left-3 bg-[var(--mw-primary)] text-white text-[9px] uppercase tracking-[0.22em] px-2 py-1 z-10">
                  Star cut
                </div>
              )}
              <div className="relative aspect-[4/3] overflow-hidden bg-black/5">
                <img
                  src={resolveImage(p.image_url, 480)}
                  alt={p.name}
                  width="480"
                  height="360"
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
              <div className="p-5 flex flex-col flex-1">
                <div className="text-[9px] uppercase tracking-[0.28em] text-[var(--mw-primary)] mb-1.5">
                  {p.category}
                </div>
                <h3 className="font-display text-xl text-black leading-tight">
                  {p.name}
                </h3>
                {p.cut && (
                  <div className="text-black/55 text-sm mt-1">{p.cut}</div>
                )}
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="font-display text-2xl text-black tabular-nums">
                    {formatAUD(p.price)}
                  </span>
                  <span className="text-black/50 text-xs uppercase tracking-[0.18em]">
                    / {p.unit}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => onAddOne(p)}
                  data-testid={`recipe-pairing-add-${p.id}`}
                  disabled={inCart}
                  className={`mt-5 inline-flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.22em] px-4 py-3 transition-colors ${
                    inCart
                      ? 'border border-emerald-500 text-emerald-700 cursor-default'
                      : 'border border-black/20 hover:border-black text-black hover:bg-black hover:text-white'
                  }`}
                >
                  {inCart ? (
                    <>
                      <Check size={13} /> In cart
                    </>
                  ) : (
                    <>
                      <ShoppingCart size={13} /> Add to cart
                    </>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
